(() => {
  'use strict';
  const { clamp, round, inchesToCm, cmToInches, announce, downloadBlob } = window.PFUtils;

  const placements = {
    sofa: { label: 'sofa', low: .60, high: .75, clearance: [6, 10], center: [57, 60] },
    bed: { label: 'bed or headboard', low: .60, high: .80, clearance: [8, 12], center: [57, 62] },
    console: { label: 'console or sideboard', low: .60, high: .75, clearance: [6, 10], center: [57, 60] },
    wall: { label: 'clear wall', low: .55, high: .70, clearance: null, center: [57, 60] }
  };
  const commonPortraitSizes = [[12,16],[12,18],[16,20],[18,24],[20,30],[24,36],[30,40],[36,48],[40,60]];

  const form = document.querySelector('#size-form');
  const widthInput = document.querySelector('#reference-width');
  const unitSelect = document.querySelector('#unit');
  const unitButtons = [...document.querySelectorAll('[data-unit]')];
  const placementSelect = document.querySelector('#placement');
  const orientationSelect = document.querySelector('#orientation');
  const gapInput = document.querySelector('#panel-gap');
  const gapUnit = document.querySelector('#gap-unit');
  const gapField = document.querySelector('#gap-field');
  const candidateInput = document.querySelector('#candidate-width');

  const candidateResult = document.querySelector('#candidate-result');
  const arrangementButtons = [...document.querySelectorAll('[data-count]')];
  const svg = document.querySelector('#size-plan');
  let arrangementCount = 1;
  let previousUnit = 'in';
  let currentResult = null;

  function valueText(value, places = 1) {
    const rounded = round(value, places);
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(places);
  }

  function format(value, unit, places = 1) {
    return `${valueText(value, places)} ${unit}`;
  }

  function formatRange(low, high, unit, places = 1) {
    return `${valueText(low, places)} to ${valueText(high, places)} ${unit}`;
  }

  function toDisplay(inches, unit) {
    return unit === 'cm' ? inchesToCm(inches) : inches;
  }

  function updateCandidate(lowInches, highInches, unit) {
    const raw = Number(candidateInput.value);

    candidateResult.removeAttribute('data-status');
    if (!candidateInput.value || !Number.isFinite(raw) || raw <= 0) {
      candidateResult.textContent = 'Enter the total width of the artwork or complete arrangement.';
      return;
    }
    const candidateInches = unit === 'cm' ? cmToInches(raw) : raw;
    const places = unit === 'cm' ? 0 : 1;
    if (candidateInches >= lowInches && candidateInches <= highInches) {
      candidateResult.dataset.status = 'fits';
      candidateResult.textContent = `${format(raw, unit, places)} fits inside the recommended range.`;
      return;
    }
    const differenceInches = candidateInches < lowInches ? lowInches - candidateInches : candidateInches - highInches;
    const difference = toDisplay(differenceInches, unit);
    const direction = candidateInches < lowInches ? 'smaller' : 'wider';
    candidateResult.dataset.status = 'outside';
    candidateResult.textContent = `${format(raw, unit, places)} is about ${format(difference, unit, places)} ${direction} than the recommended range.`;
  }

  function pickCommonArrangement(referenceInches, gapInches, count, orientation, placement) {
    const low = referenceInches * placement.low;
    const high = referenceInches * placement.high;
    const midpoint = (low + high) / 2;
    return commonPortraitSizes
      .map(([portraitW, portraitH]) => {
        const panelW = orientation === 'landscape' ? portraitH : portraitW;
        const panelH = orientation === 'landscape' ? portraitW : portraitH;
        const totalW = panelW * count + gapInches * (count - 1);
        const outsidePenalty = totalW < low ? (low - totalW) * 1.35 : totalW > high ? (totalW - high) * 1.35 : 0;
        return { panelW, panelH, totalW, score: Math.abs(totalW - midpoint) + outsidePenalty };
      })
      .sort((a, b) => a.score - b.score)[0];
  }

  function makeSvg(result) {
    const { referenceInches, placementKey, arrangement, selected, lowInches, highInches, unit } = result;
    const wallX = 45, wallY = 35, wallW = 1310, wallH = 830;
    const furnitureW = placementKey === 'wall' ? 1040 : 940;
    const furnitureX = (1400 - furnitureW) / 2;
    const coverage = selected.totalW / referenceInches;
    const totalArtW = clamp(furnitureW * coverage, 190, 1140);
    const gapPx = arrangement > 1 ? clamp(totalArtW * ((result.gapInches * (arrangement - 1)) / selected.totalW) / (arrangement - 1), 12, 40) : 0;
    const panelW = (totalArtW - gapPx * (arrangement - 1)) / arrangement;
    const panelH = clamp(panelW * selected.panelH / selected.panelW, 120, 410);
    const artX = (1400 - totalArtW) / 2;
    const furnitureTop = placementKey === 'wall' ? 720 : 648;
    const artBottom = furnitureTop - (placementKey === 'wall' ? 95 : 78);
    const artY = artBottom - panelH;
    const rangeLow = furnitureW * (lowInches / referenceInches);
    const rangeHigh = furnitureW * (highInches / referenceInches);
    const rangeX = (1400 - rangeHigh) / 2;
    const unitLabel = unit === 'cm' ? 'cm' : 'in';
    const totalLabel = format(toDisplay(selected.totalW, unit), unitLabel, unit === 'cm' ? 0 : 1);
    const refLabel = format(toDisplay(referenceInches, unit), unitLabel, unit === 'cm' ? 0 : 1);
    const rangeLabel = formatRange(toDisplay(lowInches, unit), toDisplay(highInches, unit), unitLabel, unit === 'cm' ? 0 : 1);
    const referenceY = placementKey === 'wall' ? 790 : 820;
    const pieces = Array.from({ length: arrangement }, (_, index) => {
      const x = artX + index * (panelW + gapPx);
      return `<g><rect x="${round(x)}" y="${round(artY)}" width="${round(panelW)}" height="${round(panelH)}" fill="#171717"/><rect x="${round(x + 10)}" y="${round(artY + 10)}" width="${round(panelW - 20)}" height="${round(panelH - 20)}" fill="${index % 2 ? '#d8d0c4' : '#b8a694'}"/><path d="M${round(x+12)} ${round(artY+panelH-14)} L${round(x+panelW*.55)} ${round(artY+panelH*.36)} L${round(x+panelW-12)} ${round(artY+panelH-14)}Z" fill="${index % 2 ? '#80786d' : '#75695d'}" opacity=".78"/></g>`;
    }).join('');

    const furniture = placementKey === 'wall'
      ? `<line x1="180" y1="720" x2="1220" y2="720" stroke="#a9a9a9" stroke-width="3" stroke-dasharray="12 12"/><text class="svg-detail" x="700" y="756" text-anchor="middle" fill="#666" font-size="25" font-family="Arial">clear wall area · ${refLabel}</text>`
      : placementKey === 'bed'
        ? `<rect x="${furnitureX}" y="648" width="${furnitureW}" height="145" rx="18" fill="#79736e"/><rect x="${furnitureX+38}" y="680" width="${furnitureW-76}" height="113" rx="10" fill="#e5dfd7"/><rect x="${furnitureX+75}" y="666" width="${(furnitureW-170)/2}" height="52" rx="12" fill="#f5f2ed"/><rect x="${furnitureX+95+(furnitureW-170)/2}" y="666" width="${(furnitureW-170)/2}" height="52" rx="12" fill="#f5f2ed"/>`
        : placementKey === 'console'
          ? `<rect x="${furnitureX}" y="662" width="${furnitureW}" height="28" fill="#4c4139"/><rect x="${furnitureX+40}" y="690" width="28" height="105" fill="#4c4139"/><rect x="${furnitureX+furnitureW-68}" y="690" width="28" height="105" fill="#4c4139"/>`
          : `<rect x="${furnitureX}" y="665" width="${furnitureW}" height="130" rx="20" fill="#716d69"/><rect x="${furnitureX+25}" y="625" width="${furnitureW-50}" height="110" rx="28" fill="#89847f"/><line x1="${furnitureX+furnitureW/3}" y1="640" x2="${furnitureX+furnitureW/3}" y2="735" stroke="#716d69" stroke-width="5"/><line x1="${furnitureX+2*furnitureW/3}" y1="640" x2="${furnitureX+2*furnitureW/3}" y2="735" stroke="#716d69" stroke-width="5"/>`;

    svg.innerHTML = `
      <title id="plan-title">Wall art size plan</title><desc id="plan-desc">${arrangement} piece arrangement measuring ${totalLabel} across above a ${refLabel} ${placements[placementKey].label}.</desc>
      <rect width="1400" height="900" fill="#f6f2ed"/>
      <rect x="${wallX}" y="${wallY}" width="${wallW}" height="${wallH}" rx="26" fill="#fff" stroke="#ded8d0" stroke-width="3"/>
      <line x1="${rangeX}" y1="${artY-44}" x2="${rangeX+rangeHigh}" y2="${artY-44}" stroke="#ce1312" stroke-width="5"/>
      <line x1="${(1400-rangeLow)/2}" y1="${artY-57}" x2="${(1400-rangeLow)/2}" y2="${artY-31}" stroke="#ce1312" stroke-width="5"/>
      <line x1="${(1400+rangeLow)/2}" y1="${artY-57}" x2="${(1400+rangeLow)/2}" y2="${artY-31}" stroke="#ce1312" stroke-width="5"/>
      <line x1="${rangeX}" y1="${artY-57}" x2="${rangeX}" y2="${artY-31}" stroke="#ce1312" stroke-width="5"/>
      <line x1="${rangeX+rangeHigh}" y1="${artY-57}" x2="${rangeX+rangeHigh}" y2="${artY-31}" stroke="#ce1312" stroke-width="5"/>
      <text class="svg-primary" x="700" y="${artY-70}" text-anchor="middle" fill="#ce1312" font-size="30" font-weight="800" font-family="Arial">IDEAL RANGE · ${rangeLabel}</text>
      ${pieces}${furniture}
      <line x1="${artX}" y1="${artBottom+35}" x2="${artX+totalArtW}" y2="${artBottom+35}" stroke="#111" stroke-width="4"/>
      <line x1="${artX}" y1="${artBottom+20}" x2="${artX}" y2="${artBottom+50}" stroke="#111" stroke-width="4"/>
      <line x1="${artX+totalArtW}" y1="${artBottom+20}" x2="${artX+totalArtW}" y2="${artBottom+50}" stroke="#111" stroke-width="4"/>
      <rect class="svg-art-width-pill" x="${700-155}" y="${artBottom+9}" width="310" height="52" rx="26" fill="#111"/>
      <text class="svg-primary svg-art-width-label" x="700" y="${artBottom+44}" text-anchor="middle" fill="#fff" font-size="27" font-weight="800" font-family="Arial">ART WIDTH · ${totalLabel}</text>
      <line x1="${furnitureX}" y1="${referenceY}" x2="${furnitureX+furnitureW}" y2="${referenceY}" stroke="#ce1312" stroke-width="5"/>
      <line x1="${furnitureX}" y1="${referenceY-14}" x2="${furnitureX}" y2="${referenceY+14}" stroke="#ce1312" stroke-width="5"/>
      <line x1="${furnitureX+furnitureW}" y1="${referenceY-14}" x2="${furnitureX+furnitureW}" y2="${referenceY+14}" stroke="#ce1312" stroke-width="5"/>`;
  }

  function calculate() {
    const unit = unitSelect.value;
    const rawWidth = Number(widthInput.value);
    const min = unit === 'cm' ? inchesToCm(20) : 20;
    const max = unit === 'cm' ? inchesToCm(500) : 500;
    const valid = Number.isFinite(rawWidth) && rawWidth >= min && rawWidth <= max;
    document.querySelector('#width-error').classList.toggle('is-hidden', valid);
    widthInput.setAttribute('aria-invalid', String(!valid));
    if (!valid) return;

    const referenceInches = unit === 'cm' ? cmToInches(rawWidth) : rawWidth;
    const rawGap = Number(gapInput.value);
    const gapInches = unit === 'cm' ? cmToInches(rawGap || 0) : rawGap || 0;
    const placementKey = placementSelect.value;
    const placement = placements[placementKey];
    const lowInches = referenceInches * placement.low;
    const highInches = referenceInches * placement.high;
    const selected = pickCommonArrangement(referenceInches, gapInches, arrangementCount, orientationSelect.value, placement);
    const unitLabel = unit;
    const displayLow = toDisplay(lowInches, unit);
    const displayHigh = toDisplay(highInches, unit);
    const displayGap = toDisplay(gapInches, unit);
    const panelW = toDisplay(selected.panelW, unit);
    const panelH = toDisplay(selected.panelH, unit);
    const selectedTotal = toDisplay(selected.totalW, unit);
    const countLabel = arrangementCount === 1 ? 'One' : arrangementCount === 2 ? 'Two' : 'Three';
    const arrangementName = arrangementCount === 1 ? 'piece' : 'pieces';

    document.querySelector('#range-output').textContent = formatRange(displayLow, displayHigh, unitLabel, unit === 'cm' ? 0 : 1);
    document.querySelector('#range-context').textContent = `For a ${format(rawWidth, unitLabel, unit === 'cm' ? 0 : 1)} ${placement.label}`;
    document.querySelector('#reference-title').textContent = placementKey === 'wall' ? 'Wall width' : `${placement.label.charAt(0).toUpperCase()+placement.label.slice(1)} width`;
    document.querySelector('#reference-value').textContent = format(rawWidth, unitLabel, unit === 'cm' ? 0 : 1);
    document.querySelector('#coverage-output').textContent = `${Math.round(placement.low * 100)}–${Math.round(placement.high * 100)}%`;
    const dimensionPlaces = unit === 'cm' ? 0 : 1;
    const panelText = `${valueText(panelW, dimensionPlaces)} × ${valueText(panelH, dimensionPlaces)} ${unitLabel}`;
    document.querySelector('#arrangement-output').textContent = arrangementCount === 1
      ? `${countLabel} ${panelText} ${arrangementName}`
      : `${countLabel} ${panelText} ${arrangementName}, ${format(displayGap, unitLabel, unit === 'cm' ? 0 : 1)} gaps`;
    document.querySelector('#clearance-output').textContent = placement.clearance
      ? `${formatRange(toDisplay(placement.clearance[0], unit), toDisplay(placement.clearance[1], unit), unitLabel, unit === 'cm' ? 0 : 0)} above ${placement.label}`
      : 'Keep clear of edges and obstacles';
    document.querySelector('#center-output').textContent = `${formatRange(toDisplay(placement.center[0], unit), toDisplay(placement.center[1], unit), unitLabel, unit === 'cm' ? 0 : 0)} from floor`;
    updateCandidate(lowInches, highInches, unit);

    const inside = selected.totalW >= lowInches && selected.totalW <= highInches;
    const direction = selected.totalW < lowInches ? 'below' : 'above';
    document.querySelector('#explanation-output').textContent = inside
      ? `At ${format(selectedTotal, unitLabel, unit === 'cm' ? 0 : 1)} total, this common arrangement sits inside the practical range.`
      : `At ${format(selectedTotal, unitLabel, unit === 'cm' ? 0 : 1)} total, the closest common arrangement falls ${direction} the practical range. Use the range as the priority and confirm a custom size with painter’s tape.`;

    currentResult = { referenceInches, placementKey, arrangement: arrangementCount, selected, lowInches, highInches, gapInches, unit };
    makeSvg(currentResult);
  }

  function roundedBox(context,x,y,width,height,radius,fill,stroke) {
    const r=Math.min(radius,width/2,height/2);
    context.beginPath();context.moveTo(x+r,y);context.arcTo(x+width,y,x+width,y+height,r);context.arcTo(x+width,y+height,x,y+height,r);context.arcTo(x,y+height,x,y,r);context.arcTo(x,y,x+width,y,r);context.closePath();
    if(fill){context.fillStyle=fill;context.fill()}if(stroke){context.strokeStyle=stroke;context.lineWidth=2;context.stroke()}
  }

  function wrappedCanvasText(context,text,x,y,maxWidth,lineHeight,maxLines=4) {
    const words=String(text).split(/\s+/);let line='',lines=[];
    words.forEach(word=>{const trial=line?`${line} ${word}`:word;if(context.measureText(trial).width>maxWidth&&line){lines.push(line);line=word}else line=trial});
    if(line)lines.push(line);lines.slice(0,maxLines).forEach((item,index)=>context.fillText(item,x,y+index*lineHeight));
  }

  async function downloadFullPlan() {
    const clone=svg.cloneNode(true);clone.setAttribute('width','1400');clone.setAttribute('height','900');
    const source=new XMLSerializer().serializeToString(clone);const sourceUrl=URL.createObjectURL(new Blob([source],{type:'image/svg+xml;charset=utf-8'}));
    try {
      const image=await new Promise((resolve,reject)=>{const item=new Image();item.onload=()=>resolve(item);item.onerror=reject;item.src=sourceUrl});
      const canvas=document.createElement('canvas');canvas.width=1600;canvas.height=1900;const context=canvas.getContext('2d');
      context.fillStyle='#f6f2ed';context.fillRect(0,0,1600,1900);
      roundedBox(context,70,60,1460,230,28,'#fff','#ddd6cd');
      context.fillStyle='#ce1312';context.font='900 20px Arial';context.fillText('PAINTFU WALL ART SIZE PLAN',110,112);
      context.fillStyle='#111';context.font='900 72px Arial';context.fillText(document.querySelector('#range-output').textContent,110,198);
      context.fillStyle='#5f5a54';context.font='28px Arial';context.fillText(document.querySelector('#range-context').textContent,112,248);
      roundedBox(context,1230,120,230,70,35,'#111');context.fillStyle='#fff';context.font='900 27px Arial';context.textAlign='center';context.fillText(document.querySelector('#coverage-output').textContent,1345,163);context.textAlign='left';
      context.drawImage(image,100,330,1400,900);
      const referenceText=`${document.querySelector('#reference-title').textContent.toUpperCase()} · ${document.querySelector('#reference-value').textContent}`;
      context.font='900 24px Arial';const referencePillWidth=Math.min(900,Math.max(390,Math.ceil(context.measureText(referenceText).width)+110));const referencePillX=(1600-referencePillWidth)/2;
      roundedBox(context,referencePillX,1154,referencePillWidth,70,35,'#ce1312','#fff');context.fillStyle='#fff';context.textAlign='center';context.fillText(referenceText,800,1198);context.textAlign='left';
      context.fillStyle='#ce1312';context.font='900 19px Arial';context.fillText('KEY RECOMMENDATIONS',80,1312);
      context.fillStyle='#111';context.font='900 38px Arial';context.fillText('Measurements to use when you hang',80,1360);
      const cards=[['01','SUGGESTED ARRANGEMENT',document.querySelector('#arrangement-output').textContent],['02','HEIGHT ABOVE FURNITURE',document.querySelector('#clearance-output').textContent],['03','CENTRE FROM FLOOR',document.querySelector('#center-output').textContent]];
      cards.forEach((card,index)=>{const x=80+index*488;roundedBox(context,x,1400,464,240,22,'#fff','#ddd6cd');context.fillStyle='#ce1312';context.fillRect(x,1400,464,7);roundedBox(context,x+28,1430,48,48,24,'#111');context.fillStyle='#fff';context.font='900 17px Arial';context.textAlign='center';context.fillText(card[0],x+52,1462);context.textAlign='left';context.fillStyle='#6d675f';context.font='900 17px Arial';context.fillText(card[1],x+28,1512);context.fillStyle='#111';context.font='900 27px Arial';wrappedCanvasText(context,card[2],x+28,1556,408,34,3)});
      roundedBox(context,80,1680,1440,125,20,'#fff','#ddd6cd');context.fillStyle='#ce1312';context.fillRect(80,1680,8,125);context.fillStyle='#111';context.font='700 25px Arial';wrappedCanvasText(context,document.querySelector('#explanation-output').textContent,115,1732,1350,34,2);
      context.fillStyle='#6d675f';context.font='20px Arial';context.fillText('Proportional planning guidance · paintfu.com/pages/wall-art-size-and-placement-calculator',80,1855);
      const png=await new Promise(resolve=>canvas.toBlob(resolve,'image/png'));if(!png)throw new Error('PNG creation failed');downloadBlob(png,'paintfu-wall-art-size-plan.png');announce('Your complete wall-art plan is downloading.');
    } finally {URL.revokeObjectURL(sourceUrl)}
  }

  arrangementButtons.forEach(button => button.addEventListener('click', () => {
    arrangementCount = Number(button.dataset.count);
    arrangementButtons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
    gapField.classList.toggle('is-hidden', arrangementCount === 1);
    calculate();
  }));

  unitButtons.forEach(button=>button.addEventListener('click',()=>{
    if(unitSelect.value===button.dataset.unit)return;
    unitSelect.value=button.dataset.unit;
    unitSelect.dispatchEvent(new Event('change',{bubbles:true}));
  }));

  unitSelect.addEventListener('change', () => {
    unitButtons.forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.unit===unitSelect.value)));
    const next = unitSelect.value;
    if (next !== previousUnit) {
      const width = Number(widthInput.value);
      const gap = Number(gapInput.value);
      const candidate = Number(candidateInput.value);
      if (Number.isFinite(width)) widthInput.value = round(next === 'cm' ? inchesToCm(width) : cmToInches(width), next === 'cm' ? 0 : 1);
      if (Number.isFinite(gap)) gapInput.value = round(next === 'cm' ? inchesToCm(gap) : cmToInches(gap), next === 'cm' ? 0 : 1);
      if (candidateInput.value && Number.isFinite(candidate)) candidateInput.value = round(next === 'cm' ? inchesToCm(candidate) : cmToInches(candidate), next === 'cm' ? 0 : 1);
      gapUnit.textContent = next;
      previousUnit = next;
    }
    calculate();
  });

  form.addEventListener('input', event => {
    if (event.target !== unitSelect) calculate();
  });
  form.addEventListener('change', event => {
    if (event.target !== unitSelect) calculate();
  });
  document.querySelector('#download-plan').addEventListener('click', async () => {
    if (!currentResult) return;
    try { await downloadFullPlan(); }
    catch { announce('The complete PNG could not be created. Please try again.'); }
  });
  document.querySelector('#copy-plan').addEventListener('click', async () => {
    const text = [
      'Wall art size result',
      `${document.querySelector('#range-context').textContent}: ${document.querySelector('#range-output').textContent} recommended`,
      `Suggested arrangement: ${document.querySelector('#arrangement-output').textContent}`,
      `Height: ${document.querySelector('#clearance-output').textContent}`,
      `Centre: ${document.querySelector('#center-output').textContent}`
    ].join('\n');
    try { await navigator.clipboard.writeText(text); announce('Result copied.'); }
    catch { announce(text); }
  });
  calculate();
})();
